import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CountryCode } from '@domains/country';
import { SuggestionsAutoCompleteFieldItem } from '../store/api-slices/suggestions-autocomplete-docs.endpoints';
import { normalizeAutocompleteValue } from '../utils/dateNormalization';
import { mapSuggestionFieldToFormField } from '../config/fieldConfigs';

interface UseAutoFillFormFromSuggestionsParams {
  methods: UseFormReturn<any>;
  country: CountryCode;
  svcItems?: SuggestionsAutoCompleteFieldItem[];
  taskNid?: string | null;
}

/**
 * Autocompleta el formulario con valores por defecto del servicio de suggestions (CO y MX)
 * Maneja dependencias entre campos, como adminPrice → hasAdmin, garage → hasGarage, etc.
 */
export const useAutoFillFormFromSuggestions = ({
  methods,
  country,
  svcItems,
  taskNid,
}: UseAutoFillFormFromSuggestionsParams) => {
  const autoFillFieldRef = useRef<string | null>(null);
  const hasAutoFilledRef = useRef(new Set<string>());

  useEffect(() => {
    hasAutoFilledRef.current.clear();
  }, [country, taskNid]);

  useEffect(() => {
    // Aplicar autocompletado para CO y MX
    if (country !== CountryCode.CO && country !== CountryCode.MX) return;
    if (!svcItems?.length) return;

    // Campos excluidos del autocompletado por país (campos calculados)
    const excludedFieldsByCountry: Record<CountryCode, string[]> = {
      [CountryCode.CO]: ['yearsOld'], // yearsOld: calculado desde constructionDate
      [CountryCode.MX]: [],
    };

    const excludedFields = excludedFieldsByCountry[country] || [];

    // Dependencias entre campos por país
    const fieldDependenciesByCountry: Record<
      CountryCode,
      Record<string, { parent: string; condition: (value: any) => boolean }>
    > = {
      [CountryCode.CO]: {
        adminPrice: { parent: 'hasAdmin', condition: v => v === 1 },
        garage: { parent: 'hasGarage', condition: v => v === 'Si' },
        freeArea: { parent: 'hasTerrace', condition: v => v === 1 },
      },
      [CountryCode.MX]: {
        adminPrice: { parent: 'propertyType', condition: v => Number(v) === 3 },
        garage: { parent: 'hasGarageDoc', condition: v => v === 'Si' },
      },
    };

    const fieldDependencies = fieldDependenciesByCountry[country] || {};

    // Campos booleanos/condicionales base por país
    const booleanFieldsByCountry: Record<CountryCode, string[]> = {
      [CountryCode.CO]: ['hasAdmin', 'hasGarage', 'hasTerrace'],
      [CountryCode.MX]: ['propertyType', 'hasGarageDoc'],
    };

    const booleanFields = booleanFieldsByCountry[country] || [];
    const filledBooleanValues: Record<string, any> = {};

    /**
     * Intenta autocompletar un campo con su sugerencia por defecto
     */
    const autoFillField = (item: SuggestionsAutoCompleteFieldItem) => {
      // No autocompletar campos excluidos (calculados)
      if (excludedFields.includes(item.field)) return;
      if (hasAutoFilledRef.current.has(item.field)) return;

      const defaultValue = item.suggestions?.find(
        s => s.default
      )?.suggestedValue;
      if (defaultValue == null) return;

      // Mapear nombre de campo de suggestions a formulario
      const formFieldName = mapSuggestionFieldToFormField(item.field);

      const currentValue = methods.getValues(formFieldName as any);
      const isEmpty =
        currentValue == null ||
        (typeof currentValue === 'string' && currentValue.trim() === '');

      if (!isEmpty) return;

      const normalized = normalizeAutocompleteValue(item.field, defaultValue, country);
      if (normalized == null) return;

      autoFillFieldRef.current = formFieldName;
      methods.setValue(formFieldName as any, normalized, {
        shouldDirty: true,
        shouldValidate: true,
      });
      autoFillFieldRef.current = null;

      hasAutoFilledRef.current.add(item.field);
    };

    /**
     * Primera pasada: autocompleta los campos booleanos base (hasAdmin, hasGarage, hasTerrace)
     */
    svcItems.forEach(item => {
      // No autocompletar campos excluidos (calculados)
      if (excludedFields.includes(item.field)) return;
      if (!booleanFields.includes(item.field)) return;

      const defaultValue = item.suggestions?.find(
        s => s.default
      )?.suggestedValue;
      if (defaultValue == null) return;

      const normalized = normalizeAutocompleteValue(item.field, defaultValue, country);
      filledBooleanValues[item.field] = normalized;

      // Mapear nombre de campo de suggestions a formulario
      const formFieldName = mapSuggestionFieldToFormField(item.field);

      autoFillFieldRef.current = formFieldName;
      methods.setValue(formFieldName as any, normalized, {
        shouldDirty: true,
        shouldValidate: true,
      });
      autoFillFieldRef.current = null;

      hasAutoFilledRef.current.add(item.field);
    });

    /**
     * Segunda pasada: autocompleta los campos dependientes (solo si su padre cumple la condición)
     * y luego los campos independientes restantes
     */
    svcItems.forEach(item => {
      const dependency = fieldDependencies[item.field];

      if (dependency) {
        const parentValue =
          filledBooleanValues[dependency.parent] ??
          methods.getValues(dependency.parent as any);

        if (dependency.condition(parentValue)) {
          autoFillField(item);
        }
      } else if (!booleanFields.includes(item.field)) {
        autoFillField(item);
      }
    });
  }, [svcItems, country, methods, taskNid]);

  return { autoFillFieldRef };
};
